import { localizedAccountDisplayName, uniqueDisplayAccount } from "../utils/localizedAccountDisplayName";
// src/pages/GeneralLedger.tsx
import { accountDisplayName } from "../utils/accountDisplayName";
import { isExcludedReportPlaceholder } from "../utils/reportAccountVisibility";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/solid";

import { apiGet } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";
import { Button } from "../components/Button";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal, ymdMonthStartLocal } from "../utils/dateUtils";
import { downloadCsv } from "../utils/downloadCsv";
import { formatNumber } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { formatMMDDYYYYFromYMD } from "../utils/dateUtils";

// -------------------- Types --------------------
type LedgerRow = {
  date: string; // YYYY-MM-DD
  entry_no?: number | null; // journal_no (or 0 for opening)
  entry_id: number; // internal id (or synthetic negative for opening)
  account_id: number;
  account: string;
  description: string; // entry.description
  source: string; // source_type
  source_link?: string | null;
  debit: number;
  credit: number;
  balance?: number | null;
  voided?: boolean;
};

type Account = { id: number; name: string; type?: string | null; is_system?: boolean | null; kind?: string | null };
type ToggleOption = { id: "on" | "off"; name: string };

type SortKey = "date" | "account";
type SortDir = "asc" | "desc";
type SortOption = { id: `${SortKey}_${SortDir}`; name: string };

const ROWS_PER_PAGE = 50;

// -------------------- Helpers --------------------
const ownerize = (v: unknown) =>
  typeof v === "string"
    ? v
        .replace(/shareholders[’']\b/gi, "Owner’s")
        .replace(/shareholder[’']\b/gi, "Owner’s")
        .replace(/\bshareholders?\b/gi, "Owner")
    : v;

function fmtMoney(n?: number | null) {
  if (n == null || Number.isNaN(n)) return "";
  const rounded = Math.round(Number(n) * 100) / 100;
  const base = formatNumber(rounded);

  if (base.includes(".")) {
    const [a, bRaw] = base.split(".");
    const b = (bRaw || "").padEnd(2, "0").slice(0, 2);
    return `${a}.${b}`;
  }
  return `${base}.00`;
}

function fmtDateMDY(ymd: string) {
  return formatMMDDYYYYFromYMD(ymd);
}

function normalize(s: unknown) {
  return String(s ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function csvEscape(v: any) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCsv(headers: string[], rows: (string | number)[][]) {
  return (
    [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n") + "\n"
  );
}

// -------------------- Small menu (3 dots) — AppleClasses --------------------
function MoreMenu({
  disabled,
  onExport,
}: {
  disabled?: boolean;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "border border-gray-200 bg-white " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={iconBtn}
        aria-label={t("directory.common.more")}
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
      </button>

      {open && !disabled && (
        <div className="absolute right-0 mt-2 w-44 rounded-xl border border-gray-200 bg-white shadow-md z-50 overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onExport();
            }}
            className={`w-full text-left px-3 py-2 ${A.body} hover:bg-gray-50`}
          >
            {t("directory.common.exportCsv")}
          </button>
        </div>
      )}
    </div>
  );
}

// -------------------- Sort header (clickable) --------------------
function SortHeader({
  label,
  active,
  dir,
  onClick,
  align = "left",
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
  align?: "left" | "right";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1 select-none",
        "hover:text-gray-700 transition",
        align === "right" ? "justify-end w-full" : "",
      ].join(" ")}
    >
      <span>{label}</span>
      {active && (
        <span className="text-xs leading-none text-gray-600" aria-hidden>
          {dir === "asc" ? "↑" : "↓"}
        </span>
      )}
    </button>
  );
}

// -------------------- Main --------------------
export default function GeneralLedger() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id = "" } = useParams<{ company_id: string }>();
  const [searchParams] = useSearchParams();

  const todayStr = ymdTodayLocal();
  const monthStart = ymdMonthStartLocal(new Date());

  // AppleClasses
  const labelCls = `block ${A.label} mb-1`;
  const inputCls = A.inputBase;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";

  // ✅ locked header spacing tokens: px-2 and mb-2
  const headerInner =
    "w-full max-w-5xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  // Dr / Cr semantics (numbers only)
  const drText = "!text-emerald-700";
  const crText = "!text-rose-700";
  const drHead = "!text-emerald-600";
  const crHead = "!text-rose-600";

  const voidPillCls = `${A.pillBase} bg-red-50 text-red-700 ring-red-100`;
  const openingPillCls = `${A.pillBase} bg-gray-100 text-gray-600 ring-gray-200`;

  // 2-line clamp without Tailwind plugin
  const clamp2: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  // Mobile-safe amount widths (match General Journal)
  const amtCol = "w-[76px] min-[390px]:w-20 min-[430px]:w-24";
  const amtCell =
    `text-right ${A.num} font-medium whitespace-nowrap ` +
    "text-[12px] min-[390px]:text-[13px]";

  // Display only: do not broaden the existing badge/navigation predicate or mutate rows.
  const displayDescription = (r: LedgerRow) =>
    r.entry_id < 0 && r.entry_no === 0 && r.source === "-" &&
    !r.source_link && r.description === "Opening balance"
      ? t("generalLedger.openingBalance")
      : r.description;

  const isOpeningRow = (r: LedgerRow) => Number(r.entry_no ?? 0) === 0;
  const isClickable = (r: LedgerRow) =>
    !!r.source_link && !isOpeningRow(r) && Number(r.entry_id) > 0;

  const openSource = (r: LedgerRow) => {
    if (!isClickable(r) || !r.source_link) return;
    navigate(companyRoute(company_id, r.source_link));
  };

  // Filters
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(todayStr);

  const [accounts, setAccounts] = useState<Account[]>([
    { id: 0, name: "All Accounts" },
  ]);
  const initialAccountId = searchParams.get("account_id") || "0";
  const [accountId, setAccountId] = useState<string>(initialAccountId);

  const balanceOptions: ToggleOption[] = useMemo(
    () => [
      { id: "on", name: "On" },
      { id: "off", name: "Off" },
    ],
    [],
  );
  const [balanceId, setBalanceId] = useState<"on" | "off">("on");
  const [includeEmpty, setIncludeEmpty] = useState<boolean>(true);

  // Client-side filter/sort
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Pagination
  const [page, setPage] = useState(1);

  // Data
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load accounts
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiGet<Account[]>("/accounts");
        const filtered = (res || [])
          .filter((acc) => !isExcludedReportPlaceholder(acc))
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!mounted) return;
        setAccounts([{ id: 0, name: "All Accounts" }, ...filtered]);
      } catch {
        if (!mounted) return;
        setAccounts([{ id: 0, name: "All Accounts" }]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedAccountObj = useMemo(
    () => accounts.find((a) => String(a.id) === accountId) || null,
    [accounts, accountId],
  );
  const selectedBalanceObj = useMemo(
    () => balanceOptions.find((o) => o.id === balanceId) || balanceOptions[0],
    [balanceOptions, balanceId],
  );

  // Fetch ledger (auto)
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        let url =
          `/general-ledger?start=${start}` +
          `&end=${end}` +
          `&show_balance=${balanceId}` +
          `&include_opening=auto` +
          `&include_empty=${includeEmpty ? "true" : "false"}`;

        if (accountId !== "0") url += `&account_id=${Number(accountId)}`;

        const res = await apiGet<LedgerRow[]>(url);
        if (!mounted) return;
        setRows(Array.isArray(res) ? res : []);
      } catch {
        if (!mounted) return;
        setRows([]);
        setError("Failed to load General Ledger.");
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [start, end, accountId, balanceId, includeEmpty]);

  // Derived
  const showAccountCol = accountId === "0";
  const showBalance = balanceId === "on";

  // Filter
  const filtered = useMemo(() => {
    const q = normalize(search);
    if (!q) return rows;

    return rows.filter((r) => {
      const hay = normalize(
        [
          r.date,
          r.entry_no,
          r.account,
          r.description,
          r.source,
          r.debit,
          r.credit,
          r.balance,
          r.voided ? "voided" : "",
          isOpeningRow(r) ? "opening" : "",
        ].join(" "),
      );
      return hay.includes(q);
    });
  }, [rows, search]);

  // Sort (client-side)
  const sorted = useMemo(() => {
    const list = [...filtered];
    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    list.sort((a, b) => {
      if (sortKey === "date") {
        const cmp = a.date.localeCompare(b.date);
        if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;

        // Opening rows (entry_no=0) should appear before movements on same date
        const aNo = a.entry_no ?? 0;
        const bNo = b.entry_no ?? 0;
        const cmpNo = aNo - bNo;
        if (cmpNo !== 0) return sortDir === "asc" ? cmpNo : -cmpNo;

        return a.entry_id - b.entry_id;
      }

      const aAcc = String(ownerize(a.account) ?? "");
      const bAcc = String(ownerize(b.account) ?? "");
      const cmp = collator.compare(aAcc, bAcc);
      if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;

      const dcmp = b.date.localeCompare(a.date);
      if (dcmp !== 0) return dcmp;
      return (b.entry_no ?? 0) - (a.entry_no ?? 0);
    });

    return list;
  }, [filtered, sortKey, sortDir]);

  // Sort option dropdown
  const sortOptions: SortOption[] = useMemo(
    () => [
      { id: "date_desc", name: "Date (newest)" },
      { id: "date_asc", name: "Date (oldest)" },
      { id: "account_asc", name: "Account (A → Z)" },
      { id: "account_desc", name: "Account (Z → A)" },
    ],
    [],
  );

  const sortLabel = (o: SortOption) => {
    switch (o.id) {
      case "date_desc": return t("sales.list.dateNewest");
      case "date_asc": return t("sales.list.dateOldest");
      case "account_asc": return t("generalJournal.sort.accountAsc");
      case "account_desc": return t("generalJournal.sort.accountDesc");
    }
  };

  const selectedSortObj = useMemo(() => {
    const id = `${sortKey}_${sortDir}` as SortOption["id"];
    return sortOptions.find((o) => o.id === id) || sortOptions[0];
  }, [sortKey, sortDir, sortOptions]);

  const onToolbarSortChange = (opt: SortOption | null) => {
    const v = opt?.id ?? "date_desc";
    const [k, d] = v.split("_") as [SortKey, SortDir];
    setSortKey(k);
    setSortDir(d);
  };

  // Clickable header sort
  const handleHeaderSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  };

  // Pagination derived + clamps
  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));

  useEffect(() => {
    setPage(1);
  }, [
    start,
    end,
    accountId,
    balanceId,
    includeEmpty,
    search,
    sortKey,
    sortDir,
  ]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  const pagedRows = useMemo(() => {
    const from = (page - 1) * ROWS_PER_PAGE;
    return sorted.slice(from, from + ROWS_PER_PAGE);
  }, [sorted, page]);

  const exportCsv = () => {
    const headers = [
      "Date",
      "Journal",
      ...(showAccountCol ? ["Account"] : []),
      "Details",
      "Source",
      "Dr",
      "Cr",
      ...(showBalance ? ["Balance"] : []),
      "Voided",
    ];

    const out: (string | number)[][] = sorted.map((r) => [
      fmtDateMDY(r.date),
      r.entry_no ?? "",
      ...(showAccountCol ? [String(ownerize(accountDisplayName(r.account, r.account_id)) ?? "")] : []),
      r.description ?? "",
      r.source ?? "",
      r.debit ? Number(r.debit) : "",
      r.credit ? Number(r.credit) : "",
      ...(showBalance ? [r.balance != null ? Number(r.balance) : ""] : []),
      r.voided ? "Voided" : "",
    ]);

    const csvText = buildCsv(headers, out);
    downloadCsv(`GeneralLedger_${start}_to_${end}.csv`, csvText);
  };

  // Column widths (desktop)
  const COL_DATE = "w-[98px]";
  const COL_JOURNAL = "w-[70px]";
  const COL_ACCOUNT = "w-[210px]";
  const COL_DR = "w-[120px]";
  const COL_CR = "w-[120px]";
  const COL_BAL = "w-[130px]";

  const pagerBtnExtraCls = "h-9 px-3 rounded-xl";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header */}
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <div className="flex-1 flex justify-center pointer-events-none">
            <h2 className={`${A.title} text-center`}>{t("generalLedger.title")}</h2>
          </div>

          <button
            type="button"
            onClick={() => navigate(companyRoute(company_id, "/"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="relative z-[80] w-full max-w-5xl mx-auto px-2 sm:px-6 mb-3">
        {/* Row 1: DateRange + ⋮ (same row on mobile) */}
        <div className="flex items-end justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <ReportDateRangeFilter
              fromLabel={t("sales.shared.from")}
              toLabel={t("sales.shared.to")}
              openCalendarLabel={t("sales.shared.openCalendar")}
              companyId={company_id}
              startDate={start}
              endDate={end}
              onChange={({ startDate, endDate }) => {
                setStart(startDate);
                setEnd(endDate);
              }}
            />
          </div>

          <div className="shrink-0">
            <MoreMenu disabled={!sorted.length} onExport={exportCsv} />
          </div>
        </div>

        {/* Row 2: Search + controls */}
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="w-full md:flex-1 min-w-[220px]">
            <label className={labelCls}>{t("dashboard.search")}</label>
            <input
              className={inputCls}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("generalLedger.search")}
            />
          </div>

          <div className="w-full md:w-[220px] relative">
            <label className={labelCls}>{t("directory.common.sort")}</label>
            <AppleComboBox<SortOption>
              options={sortOptions}
              value={selectedSortObj}
              onChange={onToolbarSortChange}
              placeholder={t("generalJournal.sort.placeholder")}
              getOptionLabel={(o) => o ? sortLabel(o) : ""}
              getOptionSearchText={(o) => o.name}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
            />
          </div>

          <div className="w-full md:w-[260px] relative">
            <label className={labelCls}>{t("generalJournal.account")}</label>
            <AppleComboBox<Account>
              options={accounts}
              value={selectedAccountObj}
              onChange={(acc) => setAccountId(acc ? String(acc.id) : "0")}
              placeholder={t("generalLedger.allAccounts")}
              getOptionLabel={(a) => a ? (a.id === 0 ? t("generalLedger.allAccounts") : localizedAccountDisplayName(uniqueDisplayAccount(accounts, a.id, a.name), t, accountDisplayName(a.name, a.id))) : ""}
              getOptionSearchText={(a) => accountDisplayName(a.name, a.id)}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
            />
          </div>

          <div className="w-full md:w-[150px] relative">
            <label className={labelCls}>{t("generalLedger.balance")}</label>
            <AppleComboBox<ToggleOption>
              options={balanceOptions}
              value={selectedBalanceObj}
              onChange={(opt) => setBalanceId(opt ? opt.id : "on")}
              placeholder={t("generalLedger.onOff")}
              getOptionLabel={(o) => o ? (o.id === "on" ? t("generalLedger.on") : t("generalLedger.off")) : ""}
              getOptionSearchText={(o) => o.name}
              preserveQueryOnDisplayChange
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
            />
          </div>

          <div className="w-full md:w-auto flex items-center gap-2 md:pb-1">
            <input
              id="includeEmpty"
              type="checkbox"
              checked={includeEmpty}
              onChange={(e) => setIncludeEmpty(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-appleblue focus:ring-appleblueD"
            />
            <label htmlFor="includeEmpty" className={A.body}>
              {t("generalLedger.includeEmpty")}
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-3 text-[13px] font-medium text-red-600">
            {error === "Failed to load General Ledger." ? t("generalLedger.errors.load") : error}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="relative z-0 w-full max-w-5xl mx-auto px-2 sm:px-6 mb-10">
        <div className={`${A.card} relative z-0 overflow-visible p-4 md:p-6`}>
          {loading ? (
            <div className="text-gray-400 py-10 text-center">{t("directory.common.loading")}</div>
          ) : totalFiltered === 0 ? (
            <div className="text-gray-400 py-10 text-center">{t("generalLedger.empty")}</div>
          ) : (
            <>
              {/* Desktop table (scroll region + sticky thead) */}
              <div className="hidden md:block overflow-x-auto max-h-[70vh] overflow-y-auto relative">
                <table className="w-full text-[13px] table-fixed">
                  <colgroup>
                    <col className={COL_DATE} />
                    <col className={COL_JOURNAL} />
                    {showAccountCol && <col className={COL_ACCOUNT} />}
                    <col /> {/* Details */}
                    <col className={COL_DR} />
                    <col className={COL_CR} />
                    {showBalance && <col className={COL_BAL} />}
                  </colgroup>

                  <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
                    <tr className="text-xs text-gray-500 font-medium">
                      <th className="py-2 px-2 text-left">
                        <SortHeader
                          label={t("sales.common.date")}
                          active={sortKey === "date"}
                          dir={sortKey === "date" ? sortDir : "desc"}
                          onClick={() => handleHeaderSort("date")}
                        />
                      </th>

                      <th className="py-2 px-2 text-left">{t("generalLedger.journal")}</th>

                      {showAccountCol && (
                        <th className="py-2 px-2 text-left">
                          <SortHeader
                            label={t("generalJournal.account")}
                            active={sortKey === "account"}
                            dir={sortKey === "account" ? sortDir : "asc"}
                            onClick={() => handleHeaderSort("account")}
                          />
                        </th>
                      )}

                      <th className="py-2 px-2 text-left">{t("generalLedger.details")}</th>
                      <th className={`py-2 px-2 text-right ${drHead}`}>{t("generalJournal.debit")}</th>
                      <th className={`py-2 px-2 text-right ${crHead}`}>{t("generalJournal.credit")}</th>
                      {showBalance && (
                        <th className="py-2 px-2 text-right">{t("generalLedger.balance")}</th>
                      )}
                    </tr>
                  </thead>

                  <tbody className={loading ? "opacity-60" : ""}>
                    {pagedRows.map((r) => {
                      const acc = localizedAccountDisplayName(uniqueDisplayAccount(accounts, r.account_id, r.account), t, String(ownerize(accountDisplayName(r.account, r.account_id)) ?? ""));
                      const clickable = isClickable(r);
                      const opening = isOpeningRow(r);

                      return (
                        <tr
                          key={`${r.entry_id}-${r.account_id}-${r.debit}-${r.credit}`}
                          className={[
                            "border-b border-gray-100 last:border-b-0 transition",
                            clickable
                              ? "hover:bg-gray-50 cursor-pointer"
                              : "hover:bg-gray-50",
                            opening ? "bg-gray-50/60" : "",
                          ].join(" ")}
                          onClick={() =>
                            clickable ? openSource(r) : undefined
                          }
                          title={clickable ? t("generalJournal.openSource") : undefined}
                        >
                          <td className="py-2 px-2 whitespace-nowrap">
                            {fmtDateMDY(r.date)}
                          </td>

                          <td
                            className={`py-2 px-2 whitespace-nowrap ${A.num}`}
                          >
                            {r.entry_no ?? ""}
                          </td>

                          {showAccountCol && (
                            <td className="py-2 px-2">
                              <div
                                className="min-w-0 whitespace-normal break-words leading-snug"
                                style={clamp2}
                                title={acc}
                              >
                                {acc}
                              </div>
                            </td>
                          )}

                          <td className="py-2 px-2">
                            <div
                              className="whitespace-normal break-words leading-snug"
                              style={clamp2}
                              title={displayDescription(r)}
                            >
                              {displayDescription(r)}
                            </div>

                            <div className="mt-0.5 flex items-center gap-2">
                              {r.source ? (
                                <div
                                  className="text-xs text-gray-400 truncate"
                                  title={r.source}
                                >
                                  {r.source}
                                </div>
                              ) : null}

                              {opening ? (
                                <span className={`shrink-0 ${openingPillCls}`}>
                                  {t("generalLedger.opening")}
                                </span>
                              ) : null}

                              {r.voided ? (
                                <span className={`shrink-0 ${voidPillCls}`}>
                                  {t("purchases.status.voided")}
                                </span>
                              ) : null}
                            </div>
                          </td>

                          <td
                            className={`py-2 px-2 text-right ${A.num} ${
                              r.debit ? drText : ""
                            }`}
                          >
                            {r.debit ? fmtMoney(r.debit) : ""}
                          </td>

                          <td
                            className={`py-2 px-2 text-right ${A.num} ${
                              r.credit ? crText : ""
                            }`}
                          >
                            {r.credit ? fmtMoney(r.credit) : ""}
                          </td>

                          {showBalance && (
                            <td className={`py-2 px-2 text-right ${A.num}`}>
                              {r.balance != null ? fmtMoney(r.balance) : ""}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="block md:hidden">
                <div className="flex flex-col gap-3">
                  {pagedRows.map((r) => {
                    const acct = localizedAccountDisplayName(uniqueDisplayAccount(accounts, r.account_id, r.account), t, String(ownerize(accountDisplayName(r.account, r.account_id)) ?? ""));
                    const clickable = isClickable(r);
                    const opening = isOpeningRow(r);

                    const showSourceFooter =
                      !!r.source && r.source !== "-" && !opening;

                    return (
                      <div
                        key={`${r.entry_id}-${r.account_id}-${r.debit}-${r.credit}`}
                        className={[
                          `${A.card} p-4 transition`,
                          clickable ? "cursor-pointer hover:bg-gray-50" : "",
                          opening ? "bg-gray-50/60" : "",
                        ].join(" ")}
                        onClick={() => (clickable ? openSource(r) : undefined)}
                        title={clickable ? t("generalJournal.openSource") : undefined}
                      >
                        <div className="flex justify-between items-start mb-1 gap-3">
                          <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-gray-700">
                              {fmtDateMDY(r.date)}
                            </div>
                            <div className={`${A.label} mt-0.5`}>
                              <span className={A.num}>
                                {t("generalJournal.journalNumber", { number: r.entry_no ?? r.entry_id })}
                              </span>
                            </div>
                          </div>

                          <div className="shrink-0 flex items-center gap-2">
                            {opening ? (
                              <span className={openingPillCls}>{t("generalLedger.opening")}</span>
                            ) : null}
                            {r.voided ? (
                              <span className={voidPillCls}>{t("purchases.status.voided")}</span>
                            ) : null}
                          </div>
                        </div>

                        <div className="mt-3 rounded-xl bg-white/70 ring-1 ring-black/5 overflow-hidden">
                          <div className="flex items-center px-3 py-2 bg-gray-50/50 border-b border-gray-100 text-xs font-medium text-gray-500">
                            <div className="flex-1 min-w-0">{t("generalJournal.account")}</div>
                            <div className={`${amtCol} text-right ${drHead}`}>
                              {t("generalJournal.debit")}
                            </div>
                            <div className={`${amtCol} text-right ${crHead}`}>
                              {t("generalJournal.credit")}
                            </div>
                          </div>

                          <div className="flex items-start px-3 py-2">
                            <div className="flex-1 min-w-0 pr-2">
                              <div
                                className={`${A.body} leading-snug break-words`}
                                style={clamp2}
                                title={acct}
                              >
                                {acct}
                              </div>
                            </div>

                            <div
                              className={`${amtCol} ${amtCell} ${
                                r.debit ? drText : ""
                              }`}
                            >
                              {r.debit ? fmtMoney(r.debit) : ""}
                            </div>

                            <div
                              className={`${amtCol} ${amtCell} ${
                                r.credit ? crText : ""
                              }`}
                            >
                              {r.credit ? fmtMoney(r.credit) : ""}
                            </div>
                          </div>

                          {showBalance ? (
                            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 bg-gray-50/40">
                              <div className="text-xs font-medium text-gray-500">
                                {t("generalLedger.balance")}
                              </div>
                              <div
                                className={`${A.num} font-semibold text-gray-700`}
                              >
                                {r.balance != null ? fmtMoney(r.balance) : ""}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div
                          className={`${A.label} mt-3 leading-snug break-words`}
                          title={displayDescription(r)}
                        >
                          {displayDescription(r)}
                        </div>

                        {showSourceFooter ? (
                          <div
                            className={`${A.label} mt-1 truncate`}
                            title={r.source}
                          >
                            {t("generalLedger.source", { source: r.source })}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination — standardized (matches other lists) */}
              <div className="flex justify-between items-center mt-4 px-1">
                <span className={`${A.label} text-appleblue select-none`}>
                  {t("directory.common.showing", {
                    from: totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1,
                    to: totalFiltered === 0 ? 0 : Math.min(page * ROWS_PER_PAGE, totalFiltered),
                    total: totalFiltered,
                  })}
                </span>

                <div className="flex gap-2">
                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnExtraCls}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    type="button"
                  >
                    {t("directory.common.prev")}
                  </Button>

                  <Button
                    variant="tertiary"
                    minWidth="0px"
                    className={pagerBtnExtraCls}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    type="button"
                  >
                    {t("directory.common.next")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
